import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowCounterClockwise, FloppyDisk, Layout } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import {
  CampaignEmailBuilder,
  type CampaignEmailBuilderHandle,
} from '@/modules/campaigns/components/campaign-email-builder/campaign-email-builder';
import {
  useGetSystemEmail,
  useGetSystemEmails,
  useResetSystemEmail,
  useUpdateSystemEmail,
} from '../hooks/use-system-emails';
import { useGetBrandingSettings } from '../hooks/use-compliance-settings';
import type { SystemEmailSummary } from '../types/system-emails.types';

type LocaleOption = { value: string; label: string };
type SystemEmailErrors = Partial<
  Record<'subject' | 'bodyHtml' | 'bodyPlainText' | 'locale', string>
>;

const systemEmailErrorsFrom = (error: unknown): SystemEmailErrors => {
  if (!(error instanceof HttpError) || error.status !== 400) return {};
  const raw = Array.isArray(error.error.errors) ? error.error.errors : [];
  const mapped: SystemEmailErrors = {};
  for (const entry of raw) {
    if (typeof entry !== 'string') continue;
    const [prefix, ...message] = entry.split(':');
    const value = message.join(':').trim() || entry;
    switch (prefix.trim().toLowerCase()) {
      case 'subject':
        mapped.subject = value;
        break;
      case 'bodyhtml':
      case 'html':
        mapped.bodyHtml = value;
        break;
      case 'bodyplaintext':
        mapped.bodyPlainText = value;
        break;
      case 'locale':
        mapped.locale = value;
        break;
    }
  }
  return mapped;
};

type SystemEmail = {
  key: string;
  title: string;
  description: string;
  tags: string[];
  noPromo?: boolean;
  /** Link back from guardrails to the settings page that previews this email. */
  previewedOnLink?: { label: string; to: string };
  locales: LocaleOption[];
  defaultSubject: string;
  defaultHtml: string;
  isCustomised?: boolean;
  localised?: boolean;
};

const SYSTEM_EMAILS: SystemEmail[] = [
  {
    key: 'user.invite',
    title: 'Team invite',
    description: 'Sent when a new teammate is invited to this workspace.',
    tags: ['{{inviteUrl}}', '{{orgName}}'],
    locales: [{ value: 'en', label: 'English · Default' }],
    defaultSubject: 'You have been invited to {{orgName}}',
    defaultHtml:
      '<p>You have been invited to join {{orgName}}.</p>' +
      '<p><a href="{{inviteUrl}}">Accept invitation</a></p>',
  },
  {
    key: 'user.password-reset',
    title: 'Password reset',
    description:
      'Sent after a password reset is requested. The link is single-use and expires shortly after it is issued.',
    tags: ['{{resetUrl}}'],
    locales: [{ value: 'en', label: 'English · Default' }],
    defaultSubject: 'Reset your password',
    defaultHtml:
      '<p>We received a request to reset your password.</p>' +
      '<p><a href="{{resetUrl}}">Reset password</a></p>' +
      '<p>If you did not request this, you can safely ignore this email.</p>',
  },
  {
    key: 'user.password-changed',
    title: 'Password changed',
    description: 'Sent after a user’s password is changed, as a security notice.',
    tags: [],
    locales: [{ value: 'en', label: 'English · Default' }],
    defaultSubject: 'Your password was changed',
    defaultHtml:
      '<p>Your password was changed.</p><p>If this was not you, contact your workspace administrator immediately.</p>',
  },
  {
    key: 'contact.double-optin',
    title: 'Confirm subscription',
    description:
      'Sent to a contact after they subscribe, asking them to confirm before they start receiving campaigns. One version per language.',
    tags: ['{{confirmUrl}}', '{{org.legalName}}', '{{org.postalAddress}}'],
    noPromo: true,
    previewedOnLink: { label: 'Double opt-in', to: '/settings/opt-in' },
    locales: [
      { value: 'en', label: 'English · Default' },
      { value: 'de', label: 'German' },
      { value: 'fr', label: 'French' },
      { value: 'it', label: 'Italian' },
    ],
    defaultSubject: 'Please confirm your subscription',
    defaultHtml:
      '<p>Thanks for signing up to hear from {{org.legalName}}. Please confirm that this is your email address.</p>' +
      '<p><a href="{{confirmUrl}}" style="display:inline-block;padding:10px 20px;background:#0066B2;color:#fff;text-decoration:none;border-radius:6px;font-weight:500">Yes, subscribe me</a></p>' +
      "<p>Didn't sign up? Just ignore this email — you won't hear from us.</p>" +
      '<p>{{org.legalName}} · {{org.postalAddress}}</p>',
  },
  {
    key: 'dsar.export-ready',
    title: 'Data export ready',
    description:
      'Sent when a requested data export is ready to download, as part of handling a subject access request.',
    tags: ['{{downloadUrl}}'],
    locales: [{ value: 'en', label: 'English · Default' }],
    defaultSubject: 'Your data export is ready',
    defaultHtml:
      '<p>Your requested data export is ready.</p>' +
      '<p><a href="{{downloadUrl}}">Download your data</a></p>',
  },
  {
    key: 'send.completed',
    title: 'Send completed',
    description: 'Sent to the campaign owner once a campaign has finished sending.',
    tags: ['{{campaignName}}'],
    locales: [{ value: 'en', label: 'English · Default' }],
    defaultSubject: 'Your campaign has finished sending',
    defaultHtml:
      '<p>Your campaign {{campaignName}} has finished sending.</p>' +
      '<p>Open its report to review delivery and engagement.</p>',
  },
];

/* ─── List row ─── */
const EmailRow = ({ email, onSelect }: { email: SystemEmail; onSelect: () => void }) => (
  <button
    type="button"
    onClick={onSelect}
    className="flex w-full items-start gap-4 border-b border-neutral-100 px-5 py-4 text-left last:border-b-0 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
  >
    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      <Layout size={16} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="font-semibold text-neutral-900">{email.title}</p>
      <p className="mt-0.5 text-sm text-muted-foreground line-clamp-2">{email.description}</p>
    </div>
    <span className="shrink-0 self-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
      {email.isCustomised ? 'Customised' : 'Default'}
    </span>
  </button>
);

/* ─── Edit view ─── */
const EditView = ({
  email,
  defaultLocale,
  onBack,
}: {
  email: SystemEmail;
  defaultLocale: string;
  onBack: () => void;
}) => {
  const { toast } = useToast();
  const builderRef = useRef<CampaignEmailBuilderHandle>(null);
  const [subject, setSubject] = useState(email.defaultSubject);
  const [locale, setLocale] = useState(defaultLocale);
  const [errors, setErrors] = useState<SystemEmailErrors>({});
  const { data: detail, isLoading, isFetching } = useGetSystemEmail(email.key, locale);
  const updateEmail = useUpdateSystemEmail(email.key, locale);
  const resetEmail = useResetSystemEmail(email.key, locale);
  const isCustomised = detail?.isCustomised ?? email.isCustomised ?? false;
  const editorHtml = detail?.bodyHtml ?? email.defaultHtml;
  const requiredTags = detail?.requiredTags?.length
    ? detail.requiredTags
    : email.tags.map((tag) => tag.replace(/[{}]/g, ''));
  const missingTags = new Set(
    (errors.bodyHtml?.match(/\{\{\s*([^}]+?)\s*\}\}/g) ?? []).map((tag) =>
      tag.replace(/[{}\s]/g, '')
    )
  );

  useEffect(() => {
    setSubject(detail?.subject ?? email.defaultSubject);
  }, [detail?.subject, email.defaultSubject]);

  useEffect(() => {
    const available = email.locales.map((item) => item.value);
    if (!available.includes(locale)) {
      setLocale(
        available.includes(defaultLocale) ? defaultLocale : (available[0] ?? defaultLocale)
      );
    }
  }, [defaultLocale, email.locales, locale]);

  const currentLocaleLabel = email.locales.find((l) => l.value === locale)?.label ?? locale;

  const handleSave = async () => {
    setErrors({});
    try {
      const content = await builderRef.current?.exportContent();
      if (!content?.html) {
        toast({
          variant: 'destructive',
          title: 'Nothing to save',
          description: 'The email body is empty.',
        });
        return;
      }
      const saved = await updateEmail.mutateAsync({
        subject: subject.trim(),
        bodyHtml: content.html,
        bodyPlainText: detail?.bodyPlainText ?? '',
      });
      setSubject(saved.subject);
      const sanitization = saved.sanitization;
      toast({
        variant: sanitization?.count ? 'default' : 'success',
        title: sanitization?.count ? 'Saved with changes' : 'Saved',
        description: sanitization?.count
          ? `Removed unsafe content: ${sanitization.removed.join(', ')}.`
          : `"${email.title}" was saved.`,
      });
    } catch (error) {
      setErrors(systemEmailErrorsFrom(error));
      toast({
        variant: 'destructive',
        title: "Couldn't save system email",
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset "${email.title}" in ${currentLocaleLabel} to the system default?`)) {
      return;
    }
    try {
      const reset = await resetEmail.mutateAsync();
      setSubject(reset.subject);
      toast({
        variant: 'default',
        title: 'Reset to default',
        description: 'The email was reset to the system default.',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: "Couldn't reset system email",
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  return (
    <div className="flex flex-col gap-5 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1">
          <button
            type="button"
            onClick={onBack}
            className="mb-1 inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft size={14} />
            All system emails
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{email.title}</h1>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            loading={resetEmail.isPending}
            disabled={
              !detail ||
              isLoading ||
              isFetching ||
              resetEmail.isPending ||
              updateEmail.isPending
            }
            onClick={() => void handleReset()}
          >
            <ArrowCounterClockwise size={15} />
            Reset to default
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={
              !detail ||
              isLoading ||
              isFetching ||
              updateEmail.isPending ||
              resetEmail.isPending
            }
            onClick={() => void handleSave()}
          >
            <FloppyDisk size={15} />
            {updateEmail.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">{email.description}</p>

      {/* Language / toolbar row */}
      <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
        <CardContent className="flex flex-wrap items-end gap-3 p-4 sm:p-5">
          {/* Language selector — only shown when the key is localised (has >1 locale option) */}
          {email.localised ? (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-neutral-500" htmlFor="se-locale">
                Language
              </label>
              <div className="flex items-center gap-2">
                <select
                  id="se-locale"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  className="rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {email.locales.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
                {isCustomised ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                    <span className="size-1.5 rounded-full bg-primary" />
                    Customised
                  </span>
                ) : (
                  <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
                    Default
                  </span>
                )}
              </div>
              {errors.locale ? (
                <p className="text-xs font-medium text-destructive" role="alert">
                  {errors.locale}
                </p>
              ) : null}
            </div>
          ) : (
            <div className="flex items-center gap-2 pb-1">
              <span className="text-sm font-medium text-neutral-700">{currentLocaleLabel}</span>
              <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500">
                Default
              </span>
            </div>
          )}
          <div className="flex-1" />
          <Button type="button" variant="outline" size="sm">
            <Layout size={15} />
            Start from a template
          </Button>
        </CardContent>
      </Card>

      {/* Guardrails + not-a-campaign */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
          <CardContent className="flex flex-col gap-3 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Must stay in this email
            </p>
            <ul className="flex flex-col gap-2">
              {requiredTags.map((rawTag) => {
                const tag = `{{${rawTag}}}`;
                const isMissing = missingTags.has(rawTag.replace(/\s/g, ''));
                return (
                  <li key={tag} className="flex items-center gap-2 text-sm">
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
                        isMissing
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {isMissing ? '×' : '✓'}
                    </span>
                    <span
                      className={`font-mono text-xs ${isMissing ? 'text-destructive' : 'text-neutral-700'}`}
                    >
                      {tag}
                    </span>
                    <span className={isMissing ? 'text-destructive' : 'text-neutral-500'}>
                      {isMissing ? 'is missing' : 'in the email'}
                    </span>
                  </li>
                );
              })}
            </ul>
            {email.noPromo && (
              <p className="flex items-start gap-1.5 border-t border-neutral-100 pt-3 text-xs text-neutral-600">
                <span className="mt-0.5 text-neutral-400">⊘</span>
                <span>
                  No promotional content. This email only asks the person to confirm — adding offers
                  or news can make it marketing that was sent without consent.
                </span>
              </p>
            )}
            {email.previewedOnLink && (
              <p className="flex items-start gap-1.5 text-xs text-neutral-600">
                <span className="mt-0.5 text-neutral-400">🔗</span>
                <span>
                  Previewed on{' '}
                  <Link
                    to={email.previewedOnLink.to}
                    className="font-medium text-primary hover:underline"
                  >
                    {email.previewedOnLink.label}
                  </Link>
                  . This is the only copy of the confirmation email.
                </span>
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
          <CardContent className="flex flex-col gap-2 p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Not a campaign
            </p>
            <p className="text-sm text-neutral-600">
              System emails are never blocked by unsubscribes or suppression, and carry no
              unsubscribe link — a password reset must reach everyone. Keep them to the one thing
              they are for.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Subject + MailCraft editor */}
      <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
        <CardContent className="flex flex-col gap-4 p-5">
          <div>
            <label
              className="mb-1.5 block text-sm font-medium text-foreground"
              htmlFor="se-subject"
            >
              Subject
            </label>
            <Input id="se-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            {errors.subject ? (
              <p className="mt-1 text-xs font-medium text-destructive" role="alert">
                {errors.subject}
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-foreground">Email</span>
              <span className="text-xs text-muted-foreground">
                Same editor as campaigns. Merge tags are in the editor&apos;s Variables tab;
                hand-edit HTML in its Code view.
              </span>
            </div>
            <CampaignEmailBuilder
              key={`${email.key}:${locale}:${editorHtml}`}
              ref={builderRef}
              html={editorHtml}
              name={email.title}
              layout="fluid"
              showInlineActions={false}
            />
            {errors.bodyHtml || errors.bodyPlainText ? (
              <p className="text-xs font-medium text-destructive" role="alert">
                {[errors.bodyHtml, errors.bodyPlainText].filter(Boolean).join(' ')}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/* ─── Page ─── */
export const SystemEmailsPage = () => {
  const [searchParams] = useSearchParams();
  const { data: summaries } = useGetSystemEmails();
  const { data: branding, isLoading: isBrandingLoading } = useGetBrandingSettings();
  const [selected, setSelected] = useState<SystemEmail | null>(null);
  const emails = useMemo(() => {
    if (!summaries?.length) return SYSTEM_EMAILS;
    const byKey = new Map<string, SystemEmailSummary>(summaries.map((item) => [item.key, item]));
    return SYSTEM_EMAILS.map((email) => {
      const summary = byKey.get(email.key);
      if (!summary) return email;
      return {
        ...email,
        title: summary.label,
        tags: summary.requiredTags.map((tag) => `{{${tag}}}`),
        locales: summary.locales.map((item) => ({
          value: item.locale,
          label: item.locale.toUpperCase(),
        })),
        defaultSubject: summary.subject,
        isCustomised: summary.isCustomised,
        localised: summary.localised,
      };
    });
  }, [summaries]);

  useEffect(() => {
    const key = searchParams.get('key');
    if (key) {
      const match = emails.find((e) => e.key === key);
      if (match) setSelected(match);
    }
  }, [emails, searchParams]);

  if (selected) {
    if (isBrandingLoading) {
      return <p className="text-sm text-muted-foreground">Loading system email settings…</p>;
    }
    return (
      <EditView
        key={`${selected.key}:${branding?.defaultLocale ?? 'fallback'}`}
        email={selected}
        defaultLocale={branding?.defaultLocale ?? selected.locales[0]?.value ?? 'en'}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div className="flex w-full max-w-[720px] flex-col gap-5 pb-8">
      <div>
        <div className="mb-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <Link to="/settings" className="hover:text-foreground">
            Settings
          </Link>
          <span>/</span>
          <span className="font-medium text-foreground">System emails</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">System emails</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Platform notifications sent to your team and contacts. There is no delete — only reset to
          default.
        </p>
      </div>

      <Card className="overflow-hidden rounded-lg border-neutral-200 bg-white shadow-sm">
        <div className="flex flex-col">
          {emails.map((email) => (
            <EmailRow key={email.key} email={email} onSelect={() => setSelected(email)} />
          ))}
        </div>
      </Card>
    </div>
  );
};
